import { api } from "@/infrastructure/api";
import { all, call, put, takeLatest } from "redux-saga/effects";
import * as A from "./actions";
import * as T from "./types";
import { AxiosResponse } from "axios";
import { firebaseDb } from "@/infrastructure/firebase";
import { notification } from "antd";
import { TradeAction } from "@/features/trades/index";

export function* deletePortfolio({ payload }: ReturnType<typeof A.deletePortfolio.request>) {
  try {
    const { data, status }: AxiosResponse<string> = yield call(
      api.get,
      `/paper-trade/delete-portfolio/${payload.portfolioId}`
    );

    if (status === 200 && data === "ok") {
      notification.success({ message: `Portfolio: ${payload.name} deleted successfully!` });
    } else {
      notification.success({ message: `Failed to delete portfolio: ${payload.name}` });
    }
    yield put(A.deletePortfolio.success());
  } catch (e) {
    yield put(A.deletePortfolio.failure());
    notification.success({ message: `Failed to delete portfolio: ${payload.name} due to ${e}` });
  }
}

export function* fetchTradeMeta() {
  try {
    const { data }: AxiosResponse<T.TradeMeta> = yield call(api.get, `/paper-trade/meta`);

    yield put(A.fetchTradeMeta.success(data));
  } catch (e) {
    yield put(A.fetchTradeMeta.failure());
  }
}

export function* findTickers({ payload }: ReturnType<typeof A.findTickers.request>) {
  try {
    const { data }: AxiosResponse<T.Tickers[]> = yield call(
      api.get,
      `/paper-trade/ticker/${payload.region}/${payload.text}`
    );

    yield put(A.findTickers.success(data));
  } catch (e) {
    yield put(A.findTickers.failure());
  }
}

export function* fetchTickerSummary({ payload }: ReturnType<typeof A.fetchTickerSummary.request>) {
  try {
    const { data }: AxiosResponse<T.TickerSummary> = yield call(api.get, `/paper-trade/ticker-summary/${payload}`);

    yield put(A.fetchTickerSummary.success(data));
  } catch (e) {
    yield put(A.fetchTickerSummary.failure());
  }
}

export function* fetchPortfolioMetrics({ payload }: ReturnType<typeof A.fetchPortfolioMetrics.request>) {
  try {
    const { data }: AxiosResponse<T.PortfolioMetrics> = yield call(
      api.get,
      `/paper-trade/portfolio-metrics/${payload.portfolioId}`
    );

    yield put(A.fetchPortfolioMetrics.success(data));
  } catch (e) {
    yield put(A.fetchPortfolioMetrics.failure());
  }
}

export function* performTrade({
  payload: { transaction, positions, cash, value }
}: ReturnType<typeof A.performTrade.request>) {
  try {
    const { data, status }: AxiosResponse<string> = yield call(api.post, `/paper-trade/transaction`, transaction);

    if (status === 200 && data === "ok") {
      firebaseDb
        .collection("paper_portfolio")
        .doc(transaction.portfolioId)
        .update({ positions: positions.filter(e => e.size > 0), cash, value })
        .then(() => {
          notification.success({ message: "Trade executed successfully!" });
        })
        .catch(e => {
          notification.error({ message: `Failed to execute trade due to: ${e}` });
        });
    } else {
      notification.error({ message: `Failed to execute trade due to: ${data}` });
    }

    yield put(TradeAction.updateSelectedTicker(""));
    yield put(A.performTrade.success());
  } catch (e) {
    yield put(A.performTrade.failure());
    notification.error({ message: `Failed to execute trade due to: ${e}` });
  }
}

export default function*() {
  yield all([
    takeLatest(A.deletePortfolio.request, deletePortfolio),
    takeLatest(A.fetchTradeMeta.request, fetchTradeMeta),
    takeLatest(A.findTickers.request, findTickers),
    takeLatest(A.fetchTickerSummary.request, fetchTickerSummary),
    takeLatest(A.fetchPortfolioMetrics.request, fetchPortfolioMetrics),
    takeLatest(A.performTrade.request, performTrade)
  ]);
}
